import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Crown, Sparkles, Check, Minus, Calendar, CreditCard, Receipt, Download, AlertTriangle, Clock,
  ArrowUpRight, ArrowDownRight, RefreshCw, Info, Loader2, ShieldCheck, Zap, History, ChevronDown, XCircle,
} from 'lucide-react';
import {
  partnerPlanApi, money, fmtDate, fmtDateTime, daysUntil, INTERVALS, STATUS, PAYMENT_STATUS, KIND_LABEL, formatLimitValue,
} from './partnerPlanApi';
import PlanCheckoutModal, { Modal } from './PlanCheckoutModal';
import { downloadReceipt } from './planReceipt';
import '../style/PartnerPlans.css';

const INTERVAL_ORDER = ['month', 'quarter', 'half_year', 'year'];
const TERMINAL = ['cancelled', 'expired'];
const EVENT_TEXT = {
  created: 'Subscription started',
  status_changed: 'Status changed',
  plan_changed: 'Plan changed',
  plan_change_scheduled: 'Plan change scheduled',
  plan_change_cancelled: 'Scheduled plan change cancelled',
  cancel_scheduled: 'Cancellation scheduled',
  cancel_unscheduled: 'Cancellation withdrawn',
  period_extended: 'Plan renewed',
  override_added: 'Custom limit applied by RestroPilots',
  override_removed: 'Custom limit removed',
  notes_updated: 'Account notes updated',
};
const CANCEL_REASONS = ['Too expensive', 'Missing features I need', 'Not using it enough', 'Switching to another tool', 'Closing / pausing my business', 'Other'];

const Pill = ({ tone = 'muted', children }) => <span className={`pp-pill pp-pill--${tone}`}>{children}</span>;

const monthlyEq = (amountMinor, interval) => Math.round(amountMinor / (INTERVALS[interval]?.months || 1));
const netOf = (sub) => Math.max(0, (sub?.billing?.amountMinor || 0) - (sub?.billing?.discountMinor || 0));

// ─── Meter ───────────────────────────────────────────────────────────────────
const Meter = ({ label, used, limit, unlimited, unit }) => {
  const fmt = (v) => (unit === 'bytes' ? formatLimitValue({ value: v, unit }) : Number(v || 0).toLocaleString('en-IN'));
  const pct = unlimited || !limit ? (limit === 0 && used > 0 ? 100 : 0) : Math.min(100, Math.round((used / limit) * 100));
  const over = !unlimited && limit !== null && used > limit;
  const tone = over || pct >= 100 ? 'bad' : pct >= 80 ? 'warn' : 'ok';
  return (
    <div className="pp-meter">
      <div className="pp-meter__top">
        <span>{label}</span>
        <span className="pp-meter__val">{fmt(used)} <em>/ {unlimited ? '∞' : fmt(limit ?? 0)}</em></span>
      </div>
      <div className="pp-meter__bar"><div className={`pp-meter__fill pp-meter__fill--${tone}`} style={{ width: `${unlimited ? 4 : pct}%` }} /></div>
      {over && <span className="pp-meter__warn">Over your plan limit — existing items stay, but you can’t add more.</span>}
      {!over && pct >= 100 && !unlimited && <span className="pp-meter__warn">Limit reached — upgrade to add more.</span>}
    </div>
  );
};

// Usage per restaurant (tables, menu items, images, screens… are limited per restaurant)
const meterTone = (r) => (r.unlimited || r.limit === null ? 'ok' : r.limit === 0 ? (r.used > 0 ? 'bad' : 'ok') : r.used >= r.limit ? 'bad' : r.used / r.limit >= 0.8 ? 'warn' : 'ok');
const worstTone = (limits) => (limits.some(l => meterTone(l) === 'bad') ? 'bad' : limits.some(l => meterTone(l) === 'warn') ? 'warn' : 'ok');

const RestaurantUsage = ({ restaurants }) => {
  const [active, setActive] = useState(restaurants[0]?.cafeId);
  const current = restaurants.find(r => r.cafeId === active) || restaurants[0];
  if (!current) return null;
  return (
    <section className="pp-card">
      <div className="pp-card__head">
        <h3>Usage by restaurant</h3>
        <span className="pp-muted">Each restaurant has its own allowance</span>
      </div>
      {restaurants.length > 1 && (
        <div className="pp-rest-tabs" role="tablist" aria-label="Restaurant">
          {restaurants.map(r => {
            const tone = worstTone(r.limits);
            return (
              <button key={r.cafeId} role="tab" aria-selected={r.cafeId === current.cafeId}
                className={`pp-rest-tab ${r.cafeId === current.cafeId ? 'pp-rest-tab--active' : ''}`} onClick={() => setActive(r.cafeId)}>
                {tone !== 'ok' && <span className={`pp-rest-dot pp-rest-dot--${tone}`} aria-label={tone === 'bad' ? 'At a limit' : 'Near a limit'} />}
                {r.name}
              </button>
            );
          })}
        </div>
      )}
      {restaurants.length === 1 && <p className="pp-muted pp-rest-name">{current.name}</p>}
      <div className="pp-meter-grid">
        {current.limits.map(l => <Meter key={l.key} label={l.label} used={l.used} limit={l.limit} unlimited={l.unlimited} unit={l.unit} />)}
      </div>
    </section>
  );
};

// ─── Overview ────────────────────────────────────────────────────────────────
const Overview = ({ me, groups, onExplore, onRenew, onCancel, onResume, onCancelScheduled, onCheckPayment, busy }) => {
  const sub = me.subscription;
  const live = sub && !TERMINAL.includes(sub.status);
  const status = sub ? STATUS[sub.status] || { label: sub.status, tone: 'muted' } : null;

  if (!sub) {
    return (
      <div className="pp-empty">
        <div className="pp-empty__icon"><Crown size={30} /></div>
        <h3>You’re not on a plan yet</h3>
        <p>Pick a plan to unlock digital menus, ordering, kitchen and reception screens, analytics and more.</p>
        {me.trial?.eligible && <p className="pp-empty__trial"><Sparkles size={15} /> Your first plan can start with a free trial — no payment needed.</p>}
        <button className="pp-btn pp-btn--primary pp-btn--lg" onClick={onExplore}>Explore plans</button>
      </div>
    );
  }

  const periodStart = new Date(sub.period.currentPeriodStart).getTime();
  const periodEnd = new Date(sub.status === 'trialing' ? sub.period.trialEndsAt : sub.period.currentPeriodEnd).getTime();
  const elapsedPct = periodEnd > periodStart ? Math.min(100, Math.max(0, ((Date.now() - periodStart) / (periodEnd - periodStart)) * 100)) : 100;
  const left = daysUntil(periodEnd);
  const graceLeft = daysUntil(sub.period.graceEndsAt);
  const renewal = me.renewal || {};
  const payLabel = renewal.quote ? `${sub.status === 'trialing' ? 'Subscribe now' : ['pending_payment', 'suspended', 'grace_period', 'past_due'].includes(sub.status) ? 'Pay now' : 'Renew now'} · ${money(renewal.quote.quote.totalMinor, renewal.quote.quote.currency)}` : null;

  const plan = me.plan;
  const enabledByGroup = groups
    .map(g => ({ ...g, items: (plan?.features || []).filter(f => f.group === g.key && me.entitlements?.features?.[f.key]) }))
    .filter(g => g.items.length);
  const keyLimits = (plan?.limits || []).filter(l => {
    const eff = me.entitlements?.limits?.[l.key];
    return eff && (eff.unlimited || eff.value > 0);
  }).map(l => ({ ...l, ...me.entitlements.limits[l.key] }));
  const keyQuotas = (plan?.quotas || []).filter(q => {
    const eff = me.entitlements?.quotas?.[q.key];
    return eff && (eff.unlimited || eff.value > 0);
  }).map(q => ({ ...q, ...me.entitlements.quotas[q.key] }));

  return (
    <div className="pp-overview">
      {/* Attention banners */}
      {me.pendingPayments?.map(p => (
        p.status === 'refund_required' ? (
          <div key={p.orderId} className="pp-banner pp-banner--warn">
            <AlertTriangle size={18} />
            <div><strong>Payment under review.</strong> We received {money(p.quote.totalMinor)} for {p.planName} but couldn’t apply it automatically. Our team will apply or refund it within 2 business days. Ref: {p.orderId}</div>
          </div>
        ) : ['paid', 'fulfilling'].includes(p.status) ? (
          <div key={p.orderId} className="pp-banner pp-banner--info">
            <Loader2 size={18} className="pp-spin" />
            <div><strong>Payment received — activating your plan.</strong> This takes a moment. <button className="pp-link" onClick={() => onCheckPayment(p.orderId)}>Refresh</button></div>
          </div>
        ) : p.status === 'created' ? (
          <div key={p.orderId} className="pp-banner pp-banner--muted">
            <Clock size={18} />
            <div>You started a payment for <strong>{p.planName}</strong> ({money(p.quote.totalMinor)}). Already paid? <button className="pp-link" onClick={() => onCheckPayment(p.orderId)} disabled={busy}>Check status</button></div>
          </div>
        ) : null
      ))}

      {sub.status === 'grace_period' && (
        <div className="pp-banner pp-banner--warn"><AlertTriangle size={18} />
          <div><strong>Your plan payment is overdue.</strong> You still have access{graceLeft !== null ? ` for ${Math.max(0, graceLeft)} more day${graceLeft === 1 ? '' : 's'}` : ''} (until {fmtDate(sub.period.graceEndsAt)}). Pay now to avoid interruption.</div>
        </div>
      )}
      {sub.status === 'past_due' && (
        <div className="pp-banner pp-banner--warn"><AlertTriangle size={18} /><div><strong>Payment past due.</strong> Please complete your payment to keep your plan active.</div></div>
      )}
      {sub.status === 'pending_payment' && (
        <div className="pp-banner pp-banner--bad"><XCircle size={18} /><div><strong>Your plan is inactive.</strong> {sub.period.trialEndsAt && new Date(sub.period.trialEndsAt) <= new Date() ? 'Your free trial has ended. ' : ''}Complete the payment to restore access.</div></div>
      )}
      {sub.status === 'suspended' && (
        <div className="pp-banner pp-banner--bad"><XCircle size={18} /><div><strong>Your plan is suspended.</strong> Plan features are paused. Pay now to restore access instantly — your data is safe.</div></div>
      )}
      {sub.status === 'trialing' && left !== null && left <= 3 && (
        <div className="pp-banner pp-banner--info"><Sparkles size={18} /><div><strong>Your free trial ends {left <= 0 ? 'today' : `in ${left} day${left === 1 ? '' : 's'}`}.</strong> Subscribe now to keep everything running.</div></div>
      )}
      {live && sub.period.cancelAtPeriodEnd && (
        <div className="pp-banner pp-banner--muted"><Info size={18} />
          <div>Your plan is set to cancel on <strong>{fmtDate(sub.period.currentPeriodEnd)}</strong>. You keep full access until then. <button className="pp-link" onClick={onResume} disabled={busy}>Keep my plan</button></div>
        </div>
      )}
      {live && sub.scheduledChange && (
        <div className="pp-banner pp-banner--muted"><ArrowDownRight size={18} />
          <div>Your plan will change to <strong>{sub.scheduledChange.planId}</strong> ({INTERVALS[sub.scheduledChange.interval]?.label}) on <strong>{fmtDate(sub.scheduledChange.effectiveAt)}</strong>. You’ll pay for the new plan when it starts. <button className="pp-link" onClick={onCancelScheduled} disabled={busy}>Cancel this change</button></div>
        </div>
      )}

      {/* Hero */}
      <section className={`pp-hero ${!live ? 'pp-hero--ended' : ''}`}>
        <div className="pp-hero__main">
          <div className="pp-hero__label"><Crown size={15} /> Current plan</div>
          <div className="pp-hero__title">
            <h2>{sub.planName}</h2>
            <Pill tone={status.tone}>{status.label}</Pill>
          </div>
          <div className="pp-hero__price">
            {sub.status === 'trialing'
              ? <span>Free until {fmtDate(sub.period.trialEndsAt)}, then {money(netOf(sub), sub.billing.currency)} / {INTERVALS[sub.billing.interval]?.per}</span>
              : <span><strong>{money(netOf(sub), sub.billing.currency)}</strong> / {INTERVALS[sub.billing.interval]?.per}{sub.billing.taxMode === 'exclusive' ? ' + GST' : ''}</span>}
          </div>

          {live && ['active', 'trialing'].includes(sub.status) && (
            <div className="pp-hero__period">
              <div className="pp-hero__period-row">
                <span><Calendar size={14} /> {sub.status === 'trialing' ? 'Trial' : 'Current period'}: {fmtDate(sub.period.currentPeriodStart)} – {fmtDate(periodEnd)}</span>
                <span className={left !== null && left <= 7 ? 'pp-warn-text' : ''}>{left > 0 ? `${left} day${left === 1 ? '' : 's'} left` : 'Ends today'}</span>
              </div>
              <div className="pp-hero__bar"><div style={{ width: `${elapsedPct}%` }} /></div>
            </div>
          )}
          {!live && <p className="pp-muted">This plan ended on {fmtDate(sub.period.endedAt || sub.period.currentPeriodEnd)}.</p>}
        </div>

        <div className="pp-hero__actions">
          {live && renewal.allowed && payLabel && (
            <button className="pp-btn pp-btn--primary" onClick={onRenew} disabled={busy}><CreditCard size={16} /> {payLabel}</button>
          )}
          {live && !renewal.allowed && renewal.opensAt && sub.status === 'active' && (
            <p className="pp-hint"><Clock size={13} /> Renewal opens {fmtDate(renewal.opensAt)}</p>
          )}
          <button className={`pp-btn ${live && renewal.allowed ? 'pp-btn--ghost' : 'pp-btn--primary'}`} onClick={onExplore} disabled={busy}>
            {live ? <><ArrowUpRight size={16} /> Change plan</> : 'Choose a plan'}
          </button>
          {live && !sub.period.cancelAtPeriodEnd && ['active', 'trialing'].includes(sub.status) && (
            <button className="pp-btn pp-btn--text" onClick={onCancel} disabled={busy}>Cancel plan</button>
          )}
        </div>
      </section>

      {me.overridesCount > 0 && (
        <p className="pp-hint pp-hint--block"><ShieldCheck size={14} /> Your account has {me.overridesCount} custom limit{me.overridesCount === 1 ? '' : 's'} arranged with RestroPilots — figures below include them.</p>
      )}

      {/* Usage */}
      {live && (me.resourceUsage?.length > 0 || me.usage?.length > 0) && (
        <section className="pp-card">
          <div className="pp-card__head"><h3>Usage</h3><span className="pp-muted">Resets monthly for usage-based limits</span></div>
          <div className="pp-meter-grid">
            {me.resourceUsage.map(r => <Meter key={r.key} label={r.label} used={r.used} limit={r.limit} unlimited={r.unlimited} unit={r.unit} />)}
            {me.usage.map(u => <Meter key={u.key} label={`${u.label} this month`} used={u.used} limit={u.limit} unlimited={u.unlimited} unit={u.unit} />)}
          </div>
        </section>
      )}

      {live && me.restaurantUsage?.length > 0 && <RestaurantUsage restaurants={me.restaurantUsage} />}

      {/* What's included */}
      {plan && live && (
        <section className="pp-card">
          <div className="pp-card__head"><h3>What’s included</h3><button className="pp-link" onClick={onExplore}>Compare plans</button></div>
          {keyLimits.length > 0 && (
            <div className="pp-limits">
              {keyLimits.map(l => (
                <div key={l.key} className="pp-limit"><span>{l.label}</span><strong>{formatLimitValue(l)}</strong><small>per {l.scope}</small></div>
              ))}
              {keyQuotas.map(q => (
                <div key={q.key} className="pp-limit"><span>{q.label}</span><strong>{formatLimitValue(q)}</strong><small>per month</small></div>
              ))}
            </div>
          )}
          <div className="pp-included">
            {enabledByGroup.map(g => (
              <div key={g.key} className="pp-included__group">
                <h4>{g.label}</h4>
                <ul>{g.items.map(f => <li key={f.key}><Check size={14} /> {f.label}</li>)}</ul>
              </div>
            ))}
            {enabledByGroup.length === 0 && <p className="pp-muted">No features are currently enabled on your plan.</p>}
          </div>
        </section>
      )}
    </div>
  );
};

// ─── Explore ─────────────────────────────────────────────────────────────────
const Explore = ({ catalog, groups, me, onSelect, busy }) => {
  const sub = me.subscription;
  const live = sub && !TERMINAL.includes(sub.status);
  const intervals = useMemo(() => INTERVAL_ORDER.filter(i => catalog.some(p => p.pricing.billingOptions.some(o => o.interval === i))), [catalog]);
  const [interval, setBillingInterval] = useState(() => (live && intervals.includes(sub.billing.interval) ? sub.billing.interval : intervals[0] || 'month'));
  const [compareOpen, setCompareOpen] = useState(false);
  const [onlyDiff, setOnlyDiff] = useState(false);

  useEffect(() => { if (intervals.length && !intervals.includes(interval)) setBillingInterval(intervals[0]); }, [intervals, interval]);

  // Best saving vs paying monthly, per interval
  const savings = useMemo(() => {
    const out = {};
    intervals.forEach(i => {
      if (i === 'month') return;
      let best = 0;
      catalog.forEach(p => {
        const m = p.pricing.billingOptions.find(o => o.interval === 'month');
        const o = p.pricing.billingOptions.find(x => x.interval === i);
        if (m && o && m.amountMinor > 0) best = Math.max(best, Math.round((1 - monthlyEq(o.amountMinor, i) / m.amountMinor) * 100));
      });
      if (best > 0) out[i] = best;
    });
    return out;
  }, [catalog, intervals]);

  const actionFor = (plan) => {
    const option = plan.pricing.billingOptions.find(o => o.interval === interval);
    if (!option) return { type: 'unavailable', label: `Not offered ${INTERVALS[interval]?.label.toLowerCase()}` };
    if (!live) {
      return { type: 'subscribe', label: 'Subscribe', trial: me.trial?.eligible && plan.pricing.trialDays > 0 };
    }
    if (sub.scheduledChange && sub.scheduledChange.planId === plan.planId && sub.scheduledChange.interval === interval) {
      return { type: 'scheduled', label: `Starts ${fmtDate(sub.scheduledChange.effectiveAt)}` };
    }
    if (sub.planId === plan.planId && sub.billing.interval === interval) {
      return sub.planVersion === plan.planVersion ? { type: 'current', label: 'Current plan' } : { type: 'change', label: 'Switch to latest version' };
    }
    if (sub.status !== 'active') return { type: 'change', label: 'Switch to this plan' };
    const longer = INTERVALS[interval].months > INTERVALS[sub.billing.interval].months;
    const higher = monthlyEq(option.amountMinor, interval) >= monthlyEq(netOf(sub), sub.billing.interval);
    if (longer && sub.planId === plan.planId) return { type: 'change', label: `Switch to ${INTERVALS[interval].label.toLowerCase()}` };
    if (higher || longer) return { type: 'change', label: 'Upgrade', icon: 'up' };
    return { type: 'downgrade', label: 'Downgrade', icon: 'down' };
  };

  if (!catalog.length) {
    return <div className="pp-empty"><h3>No plans available right now</h3><p>Please check back soon or contact RestroPilots support.</p></div>;
  }

  // Comparison rows
  const featureRows = groups.map(g => ({
    ...g,
    features: catalog[0].features.filter(f => f.group === g.key),
    limits: catalog[0].limits.filter(l => l.group === g.key),
    quotas: catalog[0].quotas.filter(q => q.group === g.key),
  })).filter(g => g.features.length + g.limits.length + g.quotas.length);
  const cell = (plan, kind, key) => plan[kind].find(x => x.key === key);
  const differs = (kind, key) => {
    const vals = catalog.map(p => JSON.stringify(kind === 'features' ? cell(p, kind, key)?.enabled : [cell(p, kind, key)?.value, cell(p, kind, key)?.unlimited]));
    return new Set(vals).size > 1;
  };

  return (
    <div className="pp-explore">
      <div className="pp-explore__head">
        <div>
          <h3>Plans that grow with your restaurant</h3>
          <p className="pp-muted">Upgrade anytime — you only pay the difference for the time left. Downgrades apply at the end of your billing period.</p>
        </div>
        {intervals.length > 1 && (
          <div className="pp-segment" role="tablist" aria-label="Billing interval">
            {intervals.map(i => (
              <button key={i} role="tab" aria-selected={interval === i} className={`pp-segment__btn ${interval === i ? 'pp-segment__btn--active' : ''}`} onClick={() => setBillingInterval(i)}>
                {INTERVALS[i].label}{savings[i] ? <span className="pp-save">−{savings[i]}%</span> : null}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="pp-plans">
        {catalog.map(plan => {
          const option = plan.pricing.billingOptions.find(o => o.interval === interval);
          const action = actionFor(plan);
          const isCurrent = action.type === 'current';
          const topLimits = plan.limits.filter(l => ['restaurants.max', 'employees.max', 'tables.max', 'screens.kitchen.max'].includes(l.key) && (l.unlimited || l.value > 0));
          const highlights = plan.highlights.length ? plan.highlights : plan.features.filter(f => f.enabled).slice(0, 6).map(f => f.label);
          return (
            <article key={plan.planId} className={`pp-plan ${plan.isFeatured ? 'pp-plan--featured' : ''} ${isCurrent ? 'pp-plan--current' : ''}`}>
              {plan.isFeatured && <div className="pp-plan__ribbon"><Zap size={12} /> {plan.badges[0] || 'Most popular'}</div>}
              <div className="pp-plan__head">
                <h4>{plan.name}</h4>
                {isCurrent && <Pill tone="good">Your plan</Pill>}
                {!plan.isFeatured && plan.badges.slice(0, 1).map(b => <Pill key={b} tone="info">{b}</Pill>)}
              </div>
              {plan.description && <p className="pp-plan__desc">{plan.description}</p>}

              <div className="pp-plan__price">
                {option ? (
                  <>
                    {option.compareAtMinor > option.amountMinor && <s>{money(option.compareAtMinor, plan.pricing.currency)}</s>}
                    <strong>{money(option.amountMinor, plan.pricing.currency)}</strong>
                    <span>/ {INTERVALS[interval].per}</span>
                    {interval !== 'month' && <small>{money(monthlyEq(option.amountMinor, interval), plan.pricing.currency)} per month, billed {INTERVALS[interval].label.toLowerCase()}</small>}
                    <small>{plan.pricing.taxMode === 'exclusive' ? `+ ${plan.pricing.gstRatePercent}% GST` : 'GST included'}{option.setupFeeMinor ? ` · ${money(option.setupFeeMinor)} one-time setup` : ''}</small>
                  </>
                ) : <span className="pp-muted">Not offered {INTERVALS[interval].label.toLowerCase()}</span>}
              </div>

              {topLimits.length > 0 && (
                <div className="pp-plan__limits">
                  {topLimits.map(l => <div key={l.key}><strong>{formatLimitValue(l)}</strong><span>{l.label.toLowerCase()}</span></div>)}
                </div>
              )}

              <ul className="pp-plan__features">
                {highlights.map(h => <li key={h}><Check size={15} /> {h}</li>)}
              </ul>

              <div className="pp-plan__cta">
                {action.trial && (
                  <button className="pp-btn pp-btn--primary pp-btn--block" onClick={() => onSelect('trial', plan, interval)} disabled={busy}>
                    <Sparkles size={16} /> Start {plan.pricing.trialDays}-day free trial
                  </button>
                )}
                <button
                  className={`pp-btn pp-btn--block ${action.trial ? 'pp-btn--ghost' : action.type === 'downgrade' ? 'pp-btn--ghost' : 'pp-btn--primary'}`}
                  disabled={busy || ['current', 'unavailable', 'scheduled'].includes(action.type)}
                  onClick={() => onSelect(action.type === 'downgrade' ? 'downgrade' : 'checkout', plan, interval)}
                >
                  {action.icon === 'up' && <ArrowUpRight size={16} />}
                  {action.icon === 'down' && <ArrowDownRight size={16} />}
                  {action.trial ? 'Subscribe now' : action.label}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <div className="pp-compare">
        <button className="pp-compare__toggle" onClick={() => setCompareOpen(o => !o)} aria-expanded={compareOpen}>
          Compare all features <ChevronDown size={16} className={compareOpen ? 'pp-rot' : ''} />
        </button>
        {compareOpen && (
          <>
            <label className="pp-check"><input type="checkbox" checked={onlyDiff} onChange={e => setOnlyDiff(e.target.checked)} /> Show only differences</label>
            <div className="pp-table-wrap">
              <table className="pp-table">
                <thead>
                  <tr><th>Feature</th>{catalog.map(p => <th key={p.planId}>{p.name}</th>)}</tr>
                </thead>
                <tbody>
                  {featureRows.map(g => {
                    const rows = [
                      ...g.limits.filter(l => !onlyDiff || differs('limits', l.key)).map(l => (
                        <tr key={l.key}><td>{l.label} <small>/ {l.scope}</small></td>{catalog.map(p => { const c = cell(p, 'limits', l.key); return <td key={p.planId}>{c && (c.unlimited || c.value > 0) ? formatLimitValue(c) : <Minus size={14} className="pp-no" />}</td>; })}</tr>
                      )),
                      ...g.quotas.filter(q => !onlyDiff || differs('quotas', q.key)).map(q => (
                        <tr key={q.key}><td>{q.label} <small>/ month</small></td>{catalog.map(p => { const c = cell(p, 'quotas', q.key); return <td key={p.planId}>{c && (c.unlimited || c.value > 0) ? formatLimitValue(c) : <Minus size={14} className="pp-no" />}</td>; })}</tr>
                      )),
                      ...g.features.filter(f => !onlyDiff || differs('features', f.key)).map(f => (
                        <tr key={f.key}><td>{f.label}</td>{catalog.map(p => <td key={p.planId}>{cell(p, 'features', f.key)?.enabled ? <Check size={16} className="pp-yes" /> : <Minus size={14} className="pp-no" />}</td>)}</tr>
                      )),
                    ];
                    if (!rows.length) return null;
                    return (
                      <React.Fragment key={g.key}>
                        <tr className="pp-table__group"><td colSpan={catalog.length + 1}>{g.label}</td></tr>
                        {rows}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Billing history ─────────────────────────────────────────────────────────
const BillingHistory = ({ notify }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try { setData(await partnerPlanApi.history()); } catch (err) { setError(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const receipt = async (orderId) => {
    setDownloading(orderId);
    try {
      const res = await partnerPlanApi.receipt(orderId);
      downloadReceipt(res);
    } catch (err) {
      notify('error', err.message);
    } finally {
      setDownloading(null);
    }
  };

  if (error) return <div className="pp-banner pp-banner--bad"><XCircle size={18} /><div>{error} <button className="pp-link" onClick={load}>Retry</button></div></div>;
  if (!data) return <div className="pp-skeleton-list">{[0, 1, 2].map(i => <div key={i} className="pp-skeleton" />)}</div>;

  const payments = data.payments.filter(p => !['created'].includes(p.status) || new Date(p.expiresAt) > new Date());

  return (
    <div className="pp-history">
      <section className="pp-card">
        <div className="pp-card__head"><h3><Receipt size={17} /> Payments</h3></div>
        {payments.length === 0 ? <p className="pp-muted">No payments yet.</p> : (
          <div className="pp-table-wrap">
            <table className="pp-table pp-table--list">
              <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Status</th><th /></tr></thead>
              <tbody>
                {payments.map(p => {
                  const st = PAYMENT_STATUS[p.status] || { label: p.status, tone: 'muted' };
                  return (
                    <tr key={p.orderId}>
                      <td data-label="Date">{fmtDate(p.paidAt || p.createdAt)}</td>
                      <td data-label="Description">
                        <strong>{KIND_LABEL[p.kind]}</strong> · {p.planName} ({INTERVALS[p.interval]?.label})
                        <div className="pp-muted pp-small">{p.receiptNumber || p.orderId}{p.paymentMethod ? ` · ${p.paymentMethod.toUpperCase()}` : ''}</div>
                      </td>
                      <td data-label="Amount" className="pp-num">{money(p.quote.totalMinor, p.quote.currency, { decimals: 2 })}</td>
                      <td data-label="Status"><Pill tone={st.tone}>{st.label}</Pill></td>
                      <td>
                        {p.status === 'fulfilled' && (
                          <button className="pp-icon-btn" onClick={() => receipt(p.orderId)} disabled={downloading === p.orderId} title="Download receipt" aria-label="Download receipt">
                            {downloading === p.orderId ? <Loader2 size={16} className="pp-spin" /> : <Download size={16} />}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="pp-history__grid">
        <section className="pp-card">
          <div className="pp-card__head"><h3><History size={17} /> Plan history</h3></div>
          {data.subscriptions.length === 0 ? <p className="pp-muted">No plans yet.</p> : (
            <ul className="pp-plan-history">
              {data.subscriptions.map(s => {
                const st = STATUS[s.status] || { label: s.status, tone: 'muted' };
                return (
                  <li key={s._id} className={s.isCurrent ? 'pp-plan-history--current' : ''}>
                    <div><strong>{s.planName}</strong> <span className="pp-muted">· {INTERVALS[s.billing.interval]?.label}</span></div>
                    <div className="pp-plan-history__meta">
                      <Pill tone={st.tone}>{st.label}</Pill>
                      <span className="pp-muted pp-small">{fmtDate(s.period.startedAt)} – {s.period.endedAt ? fmtDate(s.period.endedAt) : s.isCurrent ? 'present' : fmtDate(s.period.currentPeriodEnd)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="pp-card">
          <div className="pp-card__head"><h3><Clock size={17} /> Activity</h3></div>
          {data.events.length === 0 ? <p className="pp-muted">No activity yet.</p> : (
            <ol className="pp-timeline">
              {data.events.slice(0, 30).map(e => (
                <li key={e._id}>
                  <strong>{EVENT_TEXT[e.type] || e.type}</strong>
                  {e.type === 'status_changed' && e.toStatus && <span> — {STATUS[e.toStatus]?.label || e.toStatus}</span>}
                  {e.data?.to && <span> — {e.data.from?.planId} → {e.data.to.planId}</span>}
                  {e.type === 'period_extended' && e.data?.currentPeriodEnd && <span> — until {fmtDate(e.data.currentPeriodEnd)}</span>}
                  <div className="pp-muted pp-small">{fmtDateTime(e.createdAt)}{e.actorType === 'admin' ? ' · by RestroPilots' : e.actorType === 'system' ? ' · automatic' : ''}</div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
};

// ─── Confirmation modals ─────────────────────────────────────────────────────
const TrialModal = ({ plan, interval, onClose, onConfirm, busy }) => {
  const option = plan.pricing.billingOptions.find(o => o.interval === interval);
  return (
    <Modal title={`Start your free ${plan.name} trial`} onClose={onClose} dismissible={!busy} labelledBy="pp-trial-title"
      footer={<><button className="pp-btn pp-btn--ghost" onClick={onClose} disabled={busy}>Not now</button>
        <button className="pp-btn pp-btn--primary" onClick={onConfirm} disabled={busy}>{busy ? <Loader2 size={16} className="pp-spin" /> : <Sparkles size={16} />} Start free trial</button></>}>
      <div className="pp-trial">
        <div className="pp-trial__big">{plan.pricing.trialDays} days free</div>
        <ul className="pp-fineprint">
          <li>Full access to everything in {plan.name} — no payment details needed.</li>
          <li>Trial ends on <strong>{fmtDate(new Date(Date.now() + plan.pricing.trialDays * 86400000))}</strong>. Subscribe anytime before then to continue without interruption.</li>
          <li>After the trial: {money(option?.amountMinor, plan.pricing.currency)} / {INTERVALS[interval]?.per}{plan.pricing.taxMode === 'exclusive' ? ' + GST' : ''}. Nothing is charged automatically.</li>
          <li>The free trial is available once per account.</li>
        </ul>
      </div>
    </Modal>
  );
};

const DowngradeModal = ({ plan, interval, me, onClose, onConfirm, onNeedsPayment, busy }) => {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const needsPaymentRef = useRef(onNeedsPayment);
  needsPaymentRef.current = onNeedsPayment;

  useEffect(() => {
    let alive = true;
    partnerPlanApi.quote({ planId: plan.planId, interval })
      // Server decides: if this is actually an upgrade, hand over to checkout
      .then(q => { if (alive) (q.kind === 'downgrade' ? setQuote(q) : needsPaymentRef.current()); })
      .catch(err => alive && setError(err.message));
    return () => { alive = false; };
  }, [plan.planId, interval]);

  const current = me.plan;
  const lostFeatures = current ? current.features.filter(f => me.entitlements.features[f.key] && !plan.features.find(x => x.key === f.key)?.enabled) : [];
  const limitWarnings = (me.resourceUsage || []).map(r => {
    const target = plan.limits.find(l => l.key === r.key);
    if (!target || target.unlimited) return null;
    return r.used > target.value ? { ...r, newLimit: target.value } : null;
  }).filter(Boolean);

  return (
    <Modal title={`Downgrade to ${plan.name}?`} onClose={onClose} dismissible={!busy} labelledBy="pp-down-title"
      footer={quote && <><button className="pp-btn pp-btn--ghost" onClick={onClose} disabled={busy}>Keep current plan</button>
        <button className="pp-btn pp-btn--danger" onClick={onConfirm} disabled={busy}>{busy && <Loader2 size={16} className="pp-spin" />} Schedule downgrade</button></>}>
      {error && <div className="pp-banner pp-banner--bad"><XCircle size={16} />{error}</div>}
      {!quote && !error && <div className="pp-center"><Loader2 size={24} className="pp-spin" /></div>}
      {quote && (
        <div className="pp-checkout">
          <div className="pp-period"><Calendar size={15} /><span>Takes effect on <strong>{fmtDate(quote.effectiveAt)}</strong> — you keep your current plan until then. No charge today.</span></div>
          <p>From {fmtDate(quote.effectiveAt)} you’ll pay <strong>{money(quote.quote.totalMinor)}</strong> / {INTERVALS[interval]?.per} (incl. GST) when you renew.</p>
          {limitWarnings.length > 0 && (
            <div className="pp-banner pp-banner--warn"><AlertTriangle size={16} />
              <div><strong>You’re above {plan.name}’s limits:</strong>
                <ul>{limitWarnings.map(w => <li key={w.key}>{w.label}: you have {w.used}, {plan.name} allows {w.newLimit}</li>)}</ul>
                Nothing will be deleted, but you won’t be able to add more until you’re under the limit.
              </div>
            </div>
          )}
          {lostFeatures.length > 0 && (
            <div>
              <h4 className="pp-subtitle">You’ll lose access to</h4>
              <ul className="pp-lost">{lostFeatures.slice(0, 12).map(f => <li key={f.key}><Minus size={13} /> {f.label}</li>)}</ul>
              {lostFeatures.length > 12 && <p className="pp-muted pp-small">+ {lostFeatures.length - 12} more</p>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const CancelModal = ({ me, onClose, onConfirm, busy }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const sub = me.subscription;
  const endsAt = sub.status === 'trialing' ? sub.period.trialEndsAt : sub.period.currentPeriodEnd;
  return (
    <Modal title="Cancel your plan?" onClose={onClose} dismissible={!busy} labelledBy="pp-cancel-title"
      footer={<><button className="pp-btn pp-btn--ghost" onClick={onClose} disabled={busy}>Keep my plan</button>
        <button className="pp-btn pp-btn--danger" disabled={busy || !reason} onClick={() => onConfirm([reason, details].filter(Boolean).join(' — '))}>{busy && <Loader2 size={16} className="pp-spin" />} Cancel at period end</button></>}>
      <div className="pp-checkout">
        <p>You’ll keep full access until <strong>{fmtDate(endsAt)}</strong>. After that, plan features will stop working — your data stays safe and you can resubscribe anytime.</p>
        <fieldset className="pp-reasons">
          <legend>What’s the main reason? <span className="pp-muted">(required)</span></legend>
          {CANCEL_REASONS.map(r => (
            <label key={r} className={`pp-reason ${reason === r ? 'pp-reason--active' : ''}`}>
              <input type="radio" name="cancel-reason" value={r} checked={reason === r} onChange={() => setReason(r)} /> {r}
            </label>
          ))}
        </fieldset>
        <textarea className="pp-textarea" rows={3} placeholder="Anything we could do better? (optional)" value={details} onChange={e => setDetails(e.target.value)} maxLength={400} />
      </div>
    </Modal>
  );
};

// ─── Root ────────────────────────────────────────────────────────────────────
const PartnerPlans = () => {
  const [view, setView] = useState('overview');
  const [me, setMe] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);   // { type, plan?, interval?, request? }
  const [historyKey, setHistoryKey] = useState(0);

  const notify = useCallback((type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const load = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([partnerPlanApi.me(), partnerPlanApi.catalog()]);
      setMe(m);
      setCatalog(c.plans);
      setGroups(c.groups);
      setError('');
    } catch (err) {
      setError(err.status === 401 ? 'Your session has expired. Please log in again.' : err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      if (success) notify('success', success);
      setModal(null);
      await load();
      setHistoryKey(k => k + 1);
    } catch (err) {
      notify('error', err.message);
    } finally {
      setBusy(false);
    }
  };

  const onSelect = (type, plan, interval) => {
    if (type === 'checkout') setModal({ type: 'checkout', request: { planId: plan.planId, planVersion: plan.planVersion, interval } });
    else setModal({ type, plan, interval });
  };

  const renew = () => {
    const s = me.subscription;
    setModal({ type: 'checkout', request: { planId: s.planId, planVersion: s.planVersion, interval: s.billing.interval } });
  };

  const checkPayment = (orderId) => run(async () => {
    const res = await partnerPlanApi.verify(orderId);
    const st = res.payment.status;
    if (st === 'fulfilled') notify('success', 'Payment confirmed — your plan is updated.');
    else if (st === 'created') notify('error', 'We haven’t received this payment yet.');
    else if (st === 'expired' || st === 'cancelled') notify('error', 'That payment session expired. You were not charged.');
  });

  if (error) {
    return (
      <div className="pp-root">
        <div className="pp-banner pp-banner--bad"><XCircle size={18} /><div>{error} <button className="pp-link" onClick={load}>Try again</button></div></div>
      </div>
    );
  }
  if (!me) {
    return <div className="pp-root"><div className="pp-skeleton pp-skeleton--hero" /><div className="pp-skeleton-list">{[0, 1].map(i => <div key={i} className="pp-skeleton" />)}</div></div>;
  }

  return (
    <div className="pp-root">
      {toast && <div className={`pp-toast pp-toast--${toast.type}`} role="status">{toast.type === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}{toast.text}</div>}

      <div className="pp-header">
        <div>
          <h2>Plans & billing</h2>
          <p className="pp-muted">Manage your RestroPilots subscription, upgrades and payments.</p>
        </div>
        <button className="pp-icon-btn" onClick={() => { load(); setHistoryKey(k => k + 1); }} title="Refresh" aria-label="Refresh"><RefreshCw size={16} /></button>
      </div>

      <div className="pp-nav" role="tablist">
        {[['overview', 'My plan'], ['explore', 'Explore plans'], ['billing', 'Billing history']].map(([id, label]) => (
          <button key={id} role="tab" aria-selected={view === id} className={`pp-nav__btn ${view === id ? 'pp-nav__btn--active' : ''}`} onClick={() => setView(id)}>{label}</button>
        ))}
      </div>

      {view === 'overview' && (
        <Overview
          me={me}
          groups={groups}
          busy={busy}
          onExplore={() => setView('explore')}
          onRenew={renew}
          onCancel={() => setModal({ type: 'cancel' })}
          onResume={() => run(() => partnerPlanApi.resume(), 'Your plan will continue.')}
          onCancelScheduled={() => run(() => partnerPlanApi.cancelScheduled(), 'Scheduled plan change cancelled.')}
          onCheckPayment={checkPayment}
        />
      )}
      {view === 'explore' && <Explore catalog={catalog} groups={groups} me={me} onSelect={onSelect} busy={busy} />}
      {view === 'billing' && <BillingHistory key={historyKey} notify={notify} />}

      {modal?.type === 'checkout' && (
        <PlanCheckoutModal
          request={modal.request}
          onClose={() => { setModal(null); load(); setHistoryKey(k => k + 1); }}
          onDone={() => { load(); }}
        />
      )}
      {modal?.type === 'trial' && (
        <TrialModal plan={modal.plan} interval={modal.interval} busy={busy} onClose={() => setModal(null)}
          onConfirm={() => run(async () => { await partnerPlanApi.startTrial({ planId: modal.plan.planId, interval: modal.interval }); setView('overview'); }, `Your ${modal.plan.name} trial has started!`)} />
      )}
      {modal?.type === 'downgrade' && (
        <DowngradeModal plan={modal.plan} interval={modal.interval} me={me} busy={busy} onClose={() => setModal(null)}
          onNeedsPayment={() => setModal({ type: 'checkout', request: { planId: modal.plan.planId, planVersion: modal.plan.planVersion, interval: modal.interval } })}
          onConfirm={() => run(async () => { await partnerPlanApi.downgrade({ planId: modal.plan.planId, interval: modal.interval }); setView('overview'); }, 'Downgrade scheduled for the end of your billing period.')} />
      )}
      {modal?.type === 'cancel' && (
        <CancelModal me={me} busy={busy} onClose={() => setModal(null)}
          onConfirm={(reason) => run(() => partnerPlanApi.cancel({ reason }), 'Your plan will be cancelled at the end of the period.')} />
      )}
    </div>
  );
};

export default PartnerPlans;
